'use client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from '@/components/ui/button';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SidebarInput } from '@/components/ui/sidebar';
import { Label } from '@/components/ui/label';
import { Search } from 'lucide-react';
import { useSearchMembers, useFetchRecentShared } from '@/api/useSearchMembers';
import { useShareContent, type ShareRequestBody } from '@/api/useShareContent';
import { ShareEntityType } from '@/api/types/shareEntityType';
import { toast } from 'sonner';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { X, Send } from 'lucide-react';
import { DropdownMenuArrow } from '@radix-ui/react-dropdown-menu';
import { useIsPublic } from '@/context/PublicMode';
import { PublicSignUpDialog } from './public/PublicSignupDialog';
import { useReturnFocus } from '@/hooks/use-return-focus';

type Member = {
  id: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  profilePictureURL?: string;
  user?: {
    username?: string;
    email?: string;
  };
};

// Members can arrive from several endpoints with different name fields — the
// search endpoint, for instance, only returns `fullName`. Read them defensively
// so a missing part renders blank instead of throwing.
const getDisplayName = (member: Member) =>
  member.fullName ||
  [member.firstName, member.lastName].filter(Boolean).join(' ') ||
  member.user?.username ||
  '';

const getInitials = (member: Member) =>
  getDisplayName(member)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || '?';

const getHandle = (member: Member) =>
  member.user?.username ||
  getDisplayName(member).replace(/\s+/g, '').toLowerCase();
type ShareProps = {
  shareEntityType: ShareEntityType;
  shareEntityID: string;
  shareEntityTitle?: string;
  showSpanUnderIcon?: boolean;
  showOnlyIcon?: boolean;
  inviteMode?: boolean;
  buttonColorClasses?: string;
  sharePath?: string;
  isSharingEnabled?: boolean;
  iconClassName?: string;
  showIcon?: boolean;
  triggerText?: string;
};
const RECENT_SELECTED_KEY = 'recentlySelectedMembers';
const NUMBERS_OF_DEFULAT_MEMBERS = 20;
const SHARE_LIMIT = 10;
const SEARCH_LIMIT = 20;

export function Share({
  inviteMode = false,
  showOnlyIcon = false,
  showSpanUnderIcon = false,
  shareEntityType,
  shareEntityID,
  shareEntityTitle,
  buttonColorClasses = '',
  sharePath,
  isSharingEnabled = true,
  iconClassName = 'h-7 w-7',
  showIcon = true,
  triggerText,
}: ShareProps) {
  const [searchText, setSearchText] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const { data: recentSharedData, refetch: refetchRecentShared } =
    useFetchRecentShared(NUMBERS_OF_DEFULAT_MEMBERS);
  const {
    mutate: searchMembers,
    data: searchResults,
    isPending: isSearching,
  } = useSearchMembers();
  const { mutate: shareContent, isPending: isSharing } = useShareContent();
  const [recentSelected, setRecentSelected] = useState<Member[]>([]);
  const [showInput, setShowInput] = useState(false);
  const [showPublicSignupDialog, setShowPublicSignupDialog] = useState(false);
  const [note, setNote] = useState('');
  // Infinite scroll state
  const [allSearchResults, setAllSearchResults] = useState<Member[]>([]);
  const [searchOffset, setSearchOffset] = useState(0);
  const [hasMoreResults, setHasMoreResults] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Tabs & Emails state
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [emailsDialogOpen, setEmailsDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  // All three share dialogs are opened from menu state, not a DialogTrigger,
  // so closing one leaves focus on <body>. Send it back to the Share button.
  const shareTriggerRef = useRef<HTMLButtonElement | null>(null);
  const shareFocus = useReturnFocus();

  const [emailInput, setEmailInput] = useState('');
  const [validEmails, setValidEmails] = useState<string[]>([]);
  const isPublic = useIsPublic();
  const queryClient = useQueryClient();

  // Build default list: prioritize recently selected, then fill from API, limit to NUMBERS_OF_DEFULAT_MEMBERS
  const defaultMembers: Member[] = Array.from(
    new Map(
      (
        [
          ...(recentSelected || []),
          ...((recentSharedData as Member[]) || []),
        ] as Member[]
      ).map((m) => [m.id, m] as const)
    ).values()
  ).slice(0, NUMBERS_OF_DEFULAT_MEMBERS);

  const displayedMembers: Member[] = searchText
    ? allSearchResults
    : defaultMembers;

  // Load recent selections on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENT_SELECTED_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Member[];
        setRecentSelected(parsed);
      }
    } catch {}
  }, []);

  // Reset search state when search text changes
  useEffect(() => {
    if (searchText) {
      setAllSearchResults([]);
      setSearchOffset(0);
      setHasMoreResults(true);
      const timer = setTimeout(() => {
        searchMembers({ query: searchText, offset: 0, limit: SEARCH_LIMIT });
      }, 500);

      return () => clearTimeout(timer);
    } else {
      setAllSearchResults([]);
      setSearchOffset(0);
      setHasMoreResults(true);
    }
  }, [searchText, searchMembers]);

  // Handle search results
  useEffect(() => {
    if (searchResults && searchText) {
      const newResults = searchResults as Member[];
      if (searchOffset === 0) {
        // First search, replace all results
        setAllSearchResults(newResults);
      } else {
        // Subsequent searches, append results
        setAllSearchResults((prev) => [...prev, ...newResults]);
      }

      // Check if we have more results
      setHasMoreResults(newResults.length === SEARCH_LIMIT);
      setIsLoadingMore(false);
    }
  }, [searchResults, searchText, searchOffset]);

  // Scroll handler for infinite loading
  const handleScroll = useCallback(() => {
    if (
      !scrollContainerRef.current ||
      !searchText ||
      isLoadingMore ||
      !hasMoreResults
    )
      return;

    const { scrollTop, scrollHeight, clientHeight } =
      scrollContainerRef.current;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50; // 50px threshold

    if (isNearBottom) {
      setIsLoadingMore(true);
      const newOffset = searchOffset + SEARCH_LIMIT;
      setSearchOffset(newOffset);
      searchMembers({
        query: searchText,
        offset: newOffset,
        limit: SEARCH_LIMIT,
      });
    }
  }, [searchText, isLoadingMore, hasMoreResults, searchOffset, searchMembers]);

  // Add scroll event listener
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Picking a member removes their row from the list, so the button holding
  // focus unmounts and focus falls back to <body> — the next Tab restarts at
  // the top of the page. Remember where we were and land on the row that takes
  // the same position, so a keyboard user can keep picking.
  const pendingRowFocusRef = useRef<number | null>(null);
  useEffect(() => {
    const index = pendingRowFocusRef.current;
    if (index == null) return;
    pendingRowFocusRef.current = null;
    const rows =
      scrollContainerRef.current?.querySelectorAll<HTMLButtonElement>(
        '[data-member-row]'
      );
    if (!rows?.length) {
      // List emptied out — the search box is the sensible place to be.
      document.getElementById('search')?.focus();
      return;
    }
    rows[Math.min(index, rows.length - 1)]?.focus();
  }, [selectedMembers]);

  const toggleMember = (id: string) => {
    const isAlreadySelected = selectedMembers.includes(id);
    if (!isAlreadySelected && selectedMembers.length >= SHARE_LIMIT) {
      toast.error(
        inviteMode
          ? `You can't invite more than ${SHARE_LIMIT} members`
          : `You can't share with more than ${SHARE_LIMIT} members`
      );
      return;
    }
    setSelectedMembers((prev) =>
      isAlreadySelected ? prev.filter((x) => x !== id) : [...prev, id]
    );
    // Persist to recent selections
    const fromLists: Member[] = [
      ...allSearchResults,
      ...((recentSharedData as Member[]) || []),
      ...(recentSelected || []),
    ];
    const selected = fromLists.find((m) => m.id === id);
    if (selected) {
      setRecentSelected((prev) => {
        const updated = [selected, ...prev.filter((m) => m.id !== id)].slice(
          0,
          10
        );
        try {
          localStorage.setItem(RECENT_SELECTED_KEY, JSON.stringify(updated));
        } catch {}
        return updated;
      });
    }
    setTimeout(() => {
      setSearchText('');
    }, 100);
  };

  // Email parsing and validation
  const parseEmails = useCallback((text: string) => {
    const parts = text
      .split(/[\n,;\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const valids: string[] = [];
    const invalids: string[] = [];
    parts.forEach((p) => {
      if (emailRegex.test(p.toLowerCase())) valids.push(p.toLowerCase());
      else invalids.push(p);
    });
    return { valids: Array.from(new Set(valids)), invalids };
  }, []);

  useEffect(() => {
    const { valids } = parseEmails(emailInput);
    setValidEmails(valids);
  }, [emailInput, parseEmails]);

  const resetState = () => {
    setSelectedMembers([]);
    setShowInput(false);
    setSearchText('');
    setAllSearchResults([]);
    setSearchOffset(0);
    setHasMoreResults(true);
    setEmailInput('');
    setValidEmails([]);
  };
  const handleMembersDialogOpenChange = (open: boolean) => {
    setMembersDialogOpen(open);
    if (open) {
      // Refresh recent selections on open
      try {
        const raw = localStorage.getItem(RECENT_SELECTED_KEY);
        if (raw) {
          const parsed = JSON.parse(raw) as Member[];
          setRecentSelected(parsed);
        } else {
          setRecentSelected([]);
        }
      } catch {
        setRecentSelected([]);
      }
    } else {
      resetState();
      shareFocus.restore();
    }
  };
  const handleEmailsDialogOpenChange = (open: boolean) => {
    setEmailsDialogOpen(open);
    if (!open) {
      resetState();
      shareFocus.restore();
    }
  };

  const handleLinkDialogOpenChange = (open: boolean) => {
    setLinkDialogOpen(open);
    if (!open) shareFocus.restore();
  };
  const ShareButton = !inviteMode ? (
    <Button
      variant="outline"
      // With showOnlyIcon the visible label is dropped, leaving the trigger
      // with no accessible name at all.
      aria-label={triggerText || 'Share'}
      className={cn(
        'flex items-center ',
        showSpanUnderIcon
          ? 'flex-col border-none hover:bg-transparent shadow-none gap-1 '
          : 'flex-row border-secondary border-2 rounded-full ',

        buttonColorClasses
      )}
    >
      {showIcon && (
        <Send
          className="w-5 h-5 flex-shrink-0"
          style={{
            ...(showSpanUnderIcon && { width: '20px', height: '20px' }),
          }}
        />
      )}
      {showSpanUnderIcon && !showOnlyIcon && (
        <span className="text-sm text-gray-700 hover:text-gray-800">
          {'Share'}
        </span>
      )}
      {!showSpanUnderIcon && <span>{triggerText || 'Share'}</span>}
    </Button>
  ) : (
    <Button
      variant="outline"
      size="lg"
      style={{ height: 'min-content' }}
      className="bg-[#94877f]  text-[16px] pl-4 border-[#94877f] text-black hover:bg-[#b8afac] font-[Merriweather] rounded-3xl px-5 py-1 flex items-center gap-2"
    >
      Invite
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          marginLeft: '-6px',
          marginRight: '-12px',
          marginTop: '-4px',
          marginBottom: '-6px',
          width: '30px',
          height: '33px',
        }}
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </Button>
  );
  const handleOpenMembersDialog = () => {
    setMembersDialogOpen(true);
    refetchRecentShared();
  };

  return (
    <>
      {isPublic ? (
        <div
          onClick={(e) => {
            setShowPublicSignupDialog(true);
            e.nativeEvent.stopImmediatePropagation();
            e.preventDefault();
            e.stopPropagation();
          }}
          className="inline-block"
        >
          {ShareButton}
        </div>
      ) : (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild ref={shareTriggerRef}>
              {ShareButton}
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-[#cdc4c1] text-black  font-[Merriweather] border-none w-auto min-w-fit rounded-sm shadow-md p-1  translate-x-[+40px] translate-y-[-6px]"
            >
              <DropdownMenuArrow className="fill-[#cdc4c1]" />
              <DropdownMenuItem
                onClick={() => {
                  shareFocus.capture(shareTriggerRef.current);
                  handleOpenMembersDialog();
                }}
                className="cursor-pointer"
                disabled={shareEntityType === ShareEntityType.BUSINESS}
              >
                <span>{inviteMode ? 'Invite by name' : 'Share by name'}</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  shareFocus.capture(shareTriggerRef.current);
                  setEmailsDialogOpen(true);
                }}
                className="cursor-pointer"
              >
                <span>{inviteMode ? 'Invite by email' : 'Share by email'}</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  shareFocus.capture(shareTriggerRef.current);
                  setLinkDialogOpen(true);
                }}
                className="cursor-pointer"
              >
                <span> {inviteMode ? 'Invite by link' : 'Share by link'}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Dialog
            open={membersDialogOpen}
            onOpenChange={handleMembersDialogOpenChange}
          >
            <DialogContent
              className="sm:max-w-lg space-y-4 border-[#9f948b] rounded-3xl"
              onOpenAutoFocus={(e) => {
                e.preventDefault();
                (e.currentTarget as HTMLElement | null)?.focus();
              }}
            >
              <DialogHeader className="md:w-full">
                <DialogTitle className="text-[18px] font-[400] font-[Merriweather] not-italic">
                  {inviteMode ? 'Invite Members' : `Share with Members`}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                {/* Search Input */}
                <div className="relative mb-2 flex items-center -mt-4">
                  <Label htmlFor="search" className="sr-only">
                    Search
                  </Label>
                  <SidebarInput
                    value={searchText}
                    id="search"
                    placeholder="Search by Name"
                    className="placeholder:text-[#554E49] placeholder:text-[18px] ml-2 placeholder:font-medium h-10 pl-2 pr-9 shadow-sm rounded-full border-1 border-[#cdc4c1] flex-1 w-full"
                    onChange={({ target: { value } }) => setSearchText(value)}
                  />
                  {/* Decoration, not a control — it has no onClick, so as a
                      <button> it was an unnamed tab stop that did nothing when
                      activated. A span states the truth and drops out of the
                      accessibility tree. */}
                  <span
                    aria-hidden="true"
                    className="absolute top-1/2 right-1 mr-4 -translate-y-1/2 text-[#948780] flex items-center justify-center z-10"
                  >
                    <Search className={iconClassName} />
                  </span>
                </div>

                {selectedMembers.length > 0 && (
                  <>
                    <div className="flex flex-wrap gap-2">
                      {selectedMembers.map((id) => {
                        const member = [
                          ...(recentSharedData || []),
                          ...allSearchResults,
                        ].find((m) => m.id === id);
                        return member ? (
                          <button
                            type="button"
                            key={member.id}
                            data-selected-chip
                            aria-label={`Remove ${getDisplayName(member)}`}
                            onClick={() => toggleMember(member.id)}
                            className="flex items-center gap-2 bg-primary hover:bg-[#9f928b] rounded-full pl-3 pr-2 py-1 cursor-pointer"
                          >
                            <span className="text-sm">
                              {getDisplayName(member)}
                            </span>
                            <X className="w-4 h-4 text-gray-800" aria-hidden="true" />
                          </button>
                        ) : null;
                      })}
                    </div>
                  </>
                )}

                {/* Member List */}
                <div
                  ref={scrollContainerRef}
                  className="h-72 overflow-y-auto -mb-4"
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#888 transparent',
                  }}
                >
                  <div className="flex flex-col gap-2">
                    {isSearching && searchOffset === 0 ? (
                      <div className="flex justify-center py-4">
                        <span className="animate-pulse">Searching...</span>
                      </div>
                    ) : displayedMembers.length === 0 ? (
                      <div className="text-center py-4 text-muted-foreground">
                        No members found
                      </div>
                    ) : (
                      <>
                        {displayedMembers
                          .filter(
                            (member) => !selectedMembers.includes(member.id)
                          )
                          .map((member, rowIndex) => (
                            <button
                              type="button"
                              key={member.id}
                              data-member-row
                              // Selecting used to happen on mousedown, which
                              // the keyboard never fires — Enter and Space
                              // raise a click, and click was cancelled, so a
                              // keyboard user could reach a member but never
                              // pick one. mousedown now only blocks the focus
                              // steal (so the search box keeps focus while you
                              // click), and click does the work for mouse and
                              // keyboard alike.
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onClick={() => {
                                pendingRowFocusRef.current = rowIndex;
                                toggleMember(member.id);
                              }}
                              className="w-full text-left flex items-center justify-between p-2 rounded-md hover:bg-[#e0dcd7] transition-colors cursor-pointer"
                            >
                              <div className="flex items-center space-x-3">
                                <Avatar className="h-8 w-8 mr-1 size-10">
                                  {/* The member's name sits right beside this,
                                      so the picture is decorative — an empty
                                      alt keeps it from being read twice. */}
                                  <AvatarImage
                                    src={member.profilePictureURL || ''}
                                    alt=""
                                  />
                                  <AvatarFallback className="rounded-lg">
                                    {getInitials(member)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex flex-col">
                                  <span className="font-medium">
                                    {getDisplayName(member)}
                                  </span>
                                  <span className="text-sm text-muted-foreground">
                                    @{getHandle(member)}
                                  </span>
                                </div>
                              </div>
                            </button>
                          ))}
                        {isLoadingMore && (
                          <div className="flex justify-center py-4">
                            <span className="animate-pulse">
                              Loading more...
                            </span>
                          </div>
                        )}
                        {!hasMoreResults &&
                          searchText &&
                          displayedMembers.length > 0 && (
                            <div className="text-center py-2 text-gray-400 text-sm">
                              No more results
                            </div>
                          )}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-sm text-gray-600 flex flex-col justify-start">
                <Button
                  onClick={() => setShowInput(true)}
                  variant="link"
                  className="p-0 h-auto self-start mb-2 text-black text-[14px]"
                >
                  Add a personal note
                </Button>
                {showInput && (
                  <SidebarInput
                    type="text"
                    value={note}
                    className="border-1 border-[#9f948b]"
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Write your note..."
                  />
                )}
              </div>
              {true && (
                <Button
                  className="inline-flex -mb-3 -mt-4 px-4 py-2 bg-[#cec5c2] text-black rounded-3xl disabled:opacity-100 w-28 font-[18px] ml-auto font-bold font-[Merriweather]"
                  onClick={() => {
                    if (selectedMembers.length === 0) {
                      toast.error('Please select at least one member');
                      return;
                    }
                    const payload: ShareRequestBody = {
                      shareEntityID,
                      shareEntityType,
                      shareEntityTitle: shareEntityTitle || undefined,
                      recipientUserIDs: selectedMembers,
                      isInvite: inviteMode,
                      personalNote: note,
                    };

                    shareContent(payload, {
                      onSuccess: () => {
                        toast.success(
                          inviteMode
                            ? 'Invited successfully'
                            : 'Shared successfully'
                        );
                        // The share endpoint sends the chat message to each
                        // recipient; refresh the chat list so it shows up.
                        queryClient.invalidateQueries({ queryKey: ['chats'] });
                        setMembersDialogOpen(false);
                        resetState();
                      },
                      onError: (error: any) => {
                        const status = error?.response?.status;
                        if (status === 500) {
                          toast.error(
                            'Something went wrong on our side. Please contact support.'
                          );
                          return;
                        }
                        toast.error(
                          inviteMode
                            ? error?.response?.data?.message || 'Error inviting'
                            : error?.response?.data?.message || 'Error sharing'
                        );
                      },
                    });
                  }}
                  disabled={isSharing}
                >
                  {isSharing
                    ? inviteMode
                      ? 'Sending...'
                      : 'Sending...'
                    : inviteMode
                      ? 'Send'
                      : 'Send'}
                </Button>
              )}
            </DialogContent>
          </Dialog>

          {/* Emails Dialog */}
          <Dialog
            open={emailsDialogOpen}
            onOpenChange={handleEmailsDialogOpenChange}
          >
            <DialogContent
              className="sm:max-w-lg space-y-4 border-[#9f948b] rounded-3xl"
              onOpenAutoFocus={(e) => {
                e.preventDefault();
                (e.currentTarget as HTMLElement | null)?.focus();
              }}
            >
              <DialogHeader className="md:w-full">
                <DialogTitle className="text-[18px] font-[400] font-[Merriweather] not-italic">
                  {inviteMode
                    ? 'Invite Members by Email'
                    : `Share With Members by Email`}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-3">
                <Label
                  htmlFor="emails"
                  className="-mt-2 text-[16px] ml-2 leading-normal"
                >
                  {inviteMode
                    ? "Send an email invitation to anyone — even if they're not a member yet."
                    : "Share this with anyone — even if they're not a  member yet."}
                </Label>
                <Textarea
                  id="emails"
                  placeholder="Type or paste email addresses, separated by commas"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  className="min-h-[77px] resize-none [overflow-wrap:anywhere] placeholder:text-[15px] placeholder:text-[#554E49] placeholder:font-medium  border-[#cdc4c1] rounded-lg"
                />
                <div className="text-sm text-gray-600 flex flex-col justify-start">
                  <Button
                    onClick={() => setShowInput(true)}
                    variant="link"
                    className="p-0 h-auto self-start mb-2 text-black text-[14px]"
                  >
                    Add a personal note
                  </Button>
                  {showInput && (
                    <SidebarInput
                      type="text"
                      value={note}
                      className="border-1 border-[#9f948b]"
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Write your note..."
                    />
                  )}
                </div>
              </div>
              <Button
                className="inline-flex -mb-3 -mt-4 px-4 py-2 bg-[#cec5c2] text-black rounded-3xl disabled:opacity-100 w-28 text-[18px] ml-auto font-bold font-[Merriweather]"
                onClick={() => {
                  if (validEmails.length === 0) {
                    toast.error('Please enter at least one valid email');
                    return;
                  }
                  const payload: ShareRequestBody = {
                    shareEntityID,
                    shareEntityType,
                    shareEntityTitle: shareEntityTitle || undefined,
                    recipientUserIDs: [],
                    isInvite: inviteMode,
                    recipientEmails: validEmails,
                    personalNote: note,
                  };

                  shareContent(payload, {
                    onSuccess: () => {
                      toast.success(
                        inviteMode
                          ? 'Invited successfully'
                          : 'Shared successfully'
                      );
                      setEmailsDialogOpen(false);
                      resetState();
                    },
                    onError: () => {
                      toast.error(
                        inviteMode ? 'Error inviting' : 'Error sharing'
                      );
                    },
                  });
                }}
                disabled={isSharing || validEmails.length === 0}
              >
                {isSharing
                  ? inviteMode
                    ? 'Sending...'
                    : 'Sending...'
                  : inviteMode
                    ? 'Send'
                    : 'Send'}
              </Button>
            </DialogContent>
          </Dialog>

          {/* Link Dialog */}
          <Dialog
            open={linkDialogOpen}
            onOpenChange={handleLinkDialogOpenChange}
          >
            <DialogContent
              className="sm:max-w-lg space-y-4 border-[#9f948b] rounded-3xl"
              onOpenAutoFocus={(e) => {
                e.preventDefault();
                (e.currentTarget as HTMLElement | null)?.focus();
              }}
            >
              <DialogHeader className="md:w-full">
                <DialogTitle className="text-[18px] font-[400] font-[Merriweather] not-italic">
                  Share Invitation Link
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                <p className="text-[15px] text-[#554E49] font-medium leading-relaxed -mt-6">
                  Invite anyone to view and join this{' '}
                  {shareEntityType === ShareEntityType.GROUP
                    ? 'group'
                    : shareEntityType === ShareEntityType.BUSINESS
                      ? 'business'
                      : ''}
                  . Using this link, people can either join (if already a
                  member) or request to become a member to join the{' '}
                  {shareEntityType === ShareEntityType.GROUP
                    ? 'group'
                    : 'business'}
                  .
                </p>
                <div className="flex items-center gap-2 p-3 ">
                  <span className="flex-1 text-md font-[400] text-black mr-4 font-[Merriweather] break-all">
                    {sharePath ?? window.location.href}
                  </span>
                  <Button
                    className="inline-flex -mb-3 -mt-4 px-4 py-2 bg-[#cec5c2]  text-black rounded-3xl disabled:opacity-100 w-24 font-[18px] ml-auto font-bold font-[Merriweather]"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(
                          sharePath ?? window.location.href
                        );
                        toast.success('Link copied to clipboard');
                      } catch {
                        toast.error('Failed to copy link');
                      }
                    }}
                  >
                    Copy
                  </Button>
                </div>
                {isSharingEnabled && (
                  <div className="">
                    <p className="text-sm font-[400] mb-6 text-gray-700 whitespace-nowrap">
                      Share this link on:
                    </p>
                    <div className="flex items-center gap-3 ml-2">
                      {getSocialShareOptions(
                        sharePath ?? window.location.href
                      ).map((option) => (
                        <button
                          key={option.name}
                          onClick={option.action}
                          className=""
                          title={option.name}
                        >
                          <div
                            className={`${
                              option.name === 'Facebook'
                                ? 'w-8 h-8'
                                : 'w-10 h-10'
                            } flex items-center justify-center`}
                          >
                            {option.icon}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
      <PublicSignUpDialog
        open={showPublicSignupDialog}
        onOpenChange={setShowPublicSignupDialog}
        message={`Sign up to share ${shareEntityType} and connect with the community!`}
      />
    </>
  );
}
export function shareOptions(link: string) {
  return [
    {
      name: 'Copy link',
      icon: (
        <svg
          fill="currentColor"
          height="20"
          role="img"
          viewBox="0 0 24 24"
          width="20"
        >
          <title>Copy link</title>
          <path
            d="m9.726 5.123 1.228-1.228a6.47 6.47 0 0 1 9.15 9.152l-1.227 1.227m-4.603 4.603-1.228 1.228a6.47 6.47 0 0 1-9.15-9.152l1.227-1.227"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
          ></path>
          <line
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            x1="8.471"
            x2="15.529"
            y1="15.529"
            y2="8.471"
          ></line>
        </svg>
      ),
      action: async () => {
        try {
          await navigator.clipboard.writeText(link);
          toast.success('Link copied to clipboard');
        } catch {
          toast.error('Failed to copy link');
        }
      },
    },
    {
      name: 'X',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1024 1024"
          width="80"
          height="80"
        >
          <g
            fill="none"
            fillRule="evenodd"
            stroke="none"
            stroke-width="1"
            transform="translate(112 112)"
          >
            <path
              fill="#000"
              d="M711.111 800H88.89C39.8 800 0 760.2 0 
         711.111V88.89C0 39.8 39.8 0 88.889 0H711.11C760.2 0 800 39.8 800 88.889V711.11C800 760.2 
         760.2 800 711.111 800"
            />
            <path
              fill="#FFF"
              fillRule="nonzero"
              d="M628 623H484.942L174 179h143.058zm-126.012-37.651h56.96L300.013 216.65h-56.96z"
            />
            <path
              fill="#FFF"
              fillRule="nonzero"
              d="M219.296885 623 379 437.732409 358.114212 410 174 623z"
            />
            <path
              fill="#FFF"
              fillRule="nonzero"
              d="M409 348.387347 429.212986 377 603 177 558.330417 177z"
            />
          </g>
        </svg>
      ),
      action: () =>
        window.open(
          `https://x.com/intent/post?text=${encodeURIComponent(link)}`,
          '_blank'
        ),
    },
    {
      name: 'Facebook',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="70"
          height="70"
          version="1.1"
          id="Layer_1"
          x="0px"
          y="0px"
          viewBox="73 0 267 266.9"
          enable-background="new 73 0 267 266.9"
        >
          <path
            id="Blue_1_"
            fill="#157DC3"
            d="M321.1,262.3c7.9,0,14.2-6.4,14.2-14.2V18.8c0-7.9-6.4-14.2-14.2-14.2H91.8  C84,4.6,77.6,11,77.6,18.8v229.3c0,7.9,6.4,14.2,14.2,14.2H321.1z"
          />
          <path
            id="f"
            fill="#FFFFFF"
            d="M255.4,262.3v-99.8h33.5l5-38.9h-38.5V98.8c0-11.3,3.1-18.9,19.3-18.9l20.6,0V45  c-3.6-0.5-15.8-1.5-30-1.5c-29.7,0-50,18.1-50,51.4v28.7h-33.6v38.9h33.6v99.8H255.4z"
          />
        </svg>
      ),
      action: () =>
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            link
          )}`,
          '_blank',
          'width=600,height=400'
        ),
    },
    {
      name: 'Instagram',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1024 1024"
          width="256"
          height="256"
          aria-label="Instagram logo"
        >
          <defs>
            <linearGradient id="igGrad" x1="0" x2="1" y1="0" y2="1">
              <stop offset="0%" stop-color="#feda75" />
              <stop offset="25%" stop-color="#fa7e1e" />
              <stop offset="50%" stop-color="#d62976" />
              <stop offset="75%" stop-color="#962fbf" />
              <stop offset="100%" stop-color="#4f5bd5" />
            </linearGradient>
            <radialGradient id="vignette" cx="0.5" cy="0.4" r="0.9">
              <stop offset="60%" stop-color="#ffffff" stop-opacity="0" />
              <stop offset="100%" stop-color="#000000" stop-opacity="0.08" />
            </radialGradient>
          </defs>

          <rect
            x="64"
            y="64"
            width="896"
            height="896"
            rx="220"
            ry="220"
            fill="url(#igGrad)"
          />

          <rect
            x="64"
            y="64"
            width="896"
            height="896"
            rx="220"
            ry="220"
            fill="url(#vignette)"
          />

          <g
            fill="none"
            stroke="#ffffff"
            stroke-width="64"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect x="256" y="256" width="512" height="512" rx="120" ry="120" />

            <circle cx="704" cy="320" r="40" fill="#ffffff" stroke="none" />
            <circle
              cx="512"
              cy="480"
              r="128"
              stroke="#ffffff"
              stroke-width="64"
              fill="none"
            />
          </g>
        </svg>
      ),
      action: () => window.open('https://instagram.com', '_blank'),
    },
  ];
}

export function getSocialShareOptions(link: string) {
  // Return only social media options, excluding the copy link option
  return shareOptions(link).filter((option) => option.name !== 'Copy link');
}
